/**
 * PlanVoiceFillPanel — LK parity с MIS VoiceFillPanel (treatment_voice_fill / correct).
 */
import { useEffect, useState } from "react"
import type { AuthSession } from "../../../../types/plans"
import type { ConstructorPlan } from "../constructorTypes"
import {
  WORKFLOW_CHECKPOINT_KEYS,
  formatCheckpointSavedAt,
  savePlanWorkflowCheckpoint,
  type PlanWorkflowCheckpoint,
} from "../workflowCheckpoint"

type OutputMode = "staged" | "flat"
type WorkspaceTarget = "classic" | "constructor"

const FILL_ACTION = "treatment_voice_fill"
const CORRECT_ACTION = "treatment_voice_correct"
const UI_SURFACE = "staff_lk_treatment_voice"

function VoiceMicButton({
  onCaptured,
  disabled,
}: {
  onCaptured: (f: File) => void
  disabled?: boolean
}) {
  const [recording, setRecording] = useState(false)
  const recorderRef = { current: null as MediaRecorder | null }
  const chunksRef = { current: [] as Blob[] }

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" })
        onCaptured(new File([blob], "voice.webm", { type: blob.type }))
        setRecording(false)
      }
      recorder.start()
      recorderRef.current = recorder
      setRecording(true)
    } catch {
      setRecording(false)
    }
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => (recording ? recorderRef.current?.stop() : void start())}
      className={`rounded-md px-3 py-1.5 text-xs ${recording ? "bg-red-50 text-red-600" : "bg-slate-100"}`}
    >
      {recording ? "■ Стоп" : "🎙 Голос"}
    </button>
  )
}

function extractMisPlanId(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null
  const p = payload as Record<string, unknown>
  const apply = p.apply_result ?? p.result ?? p
  if (!apply || typeof apply !== "object") return null
  const a = apply as Record<string, unknown>
  if (typeof a.target_entity_id === "string") return a.target_entity_id
  const created = a.created_plan
  if (created && typeof created === "object" && typeof (created as { plan_id?: string }).plan_id === "string") {
    return (created as { plan_id: string }).plan_id
  }
  return null
}

export function PlanVoiceFillPanel({
  plan,
  session,
  apiBase,
  misPatientId,
  staffUserId,
  diagnosticsLinked = false,
  onApplied,
}: {
  plan: ConstructorPlan
  session: AuthSession
  apiBase: string
  misPatientId: string | null
  staffUserId: string
  diagnosticsLinked?: boolean
  onApplied: () => void
}) {
  const [inputText, setInputText] = useState("")
  const [outputMode, setOutputMode] = useState<OutputMode>("staged")
  const [workspaceTarget, setWorkspaceTarget] = useState<WorkspaceTarget>("constructor")
  const [useDiagnocat, setUseDiagnocat] = useState(false)
  const [step, setStep] = useState<"idle" | "proposing" | "preview" | "applying" | "done" | "error">("idle")
  const [message, setMessage] = useState("")
  const [proposalId, setProposalId] = useState<string | null>(null)
  const [preview, setPreview] = useState<unknown>(null)
  const [restored, setRestored] = useState(false)

  const actionKey = plan.mis_plan_id ? CORRECT_ACTION : FILL_ACTION
  const checkpointKey = actionKey === CORRECT_ACTION ? WORKFLOW_CHECKPOINT_KEYS.voiceCorrect : WORKFLOW_CHECKPOINT_KEYS.voiceFill
  const savedCheckpoint = plan.workflow_checkpoints?.[checkpointKey] ?? null

  useEffect(() => {
    if (restored || step !== "idle") return
    const cp = savedCheckpoint
    if (!cp || cp.step !== "preview" || cp.preview == null) return
    if (cp.input_text) setInputText(cp.input_text)
    if (cp.proposal_id) setProposalId(cp.proposal_id)
    setPreview(cp.preview)
    setStep("preview")
    setRestored(true)
  }, [savedCheckpoint, restored, step])

  const persistCheckpoint = async (payload: PlanWorkflowCheckpoint) => {
    await savePlanWorkflowCheckpoint(apiBase, session, plan.id, checkpointKey, payload)
  }

  const authHeader = { Authorization: `Bearer ${session.accessToken}` }

  const buildMetadata = () => ({
    consumer: "staff_lk_ai",
    authoring_clinician_id: staffUserId,
    output_mode: outputMode,
    workspace_target: workspaceTarget,
    ...(diagnosticsLinked && useDiagnocat ? { use_diagnocat_findings: true } : {}),
  })

  const propose = async (text: string) => {
    if (!misPatientId) {
      setStep("error")
      setMessage("Пациент не связан с MIS — голосовое составление недоступно.")
      return
    }
    if (!text.trim()) return
    setStep("proposing")
    void persistCheckpoint({ step: "proposing", input_text: text.trim() })
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({
          action_key: actionKey,
          ui_surface: UI_SURFACE,
          source_kind: "text",
          input_text: text.trim(),
          patient_id: misPatientId,
          target_entity_id: plan.mis_plan_id ?? null,
          metadata: buildMetadata(),
        }),
      })
      if (!res.ok) throw new Error(`proposal HTTP ${res.status}`)
      const data = (await res.json()) as { proposal: { proposal_id: string; proposed_payload: unknown } }
      setProposalId(data.proposal.proposal_id)
      setPreview(data.proposal.proposed_payload)
      setStep("preview")
      void persistCheckpoint({ step: "preview", input_text: text.trim(), proposal_id: data.proposal.proposal_id, preview: data.proposal.proposed_payload, last_error: null })
    } catch (e) {
      const err = String(e)
      setStep("error")
      setMessage(err)
      void persistCheckpoint({ step: "error", input_text: text.trim(), last_error: err, preview: preview ?? savedCheckpoint?.preview ?? null, proposal_id: proposalId })
    }
  }

  const transcribe = async (file: File) => {
    const fd = new FormData()
    fd.set("action_key", actionKey)
    fd.set("ui_surface", UI_SURFACE)
    fd.set("patient_id", misPatientId ?? "")
    fd.set("audio", file)
    const res = await fetch(`${apiBase}/staff/ai-assist/voice/transcribe`, {
      method: "POST",
      headers: authHeader,
      body: fd,
    })
    if (!res.ok) throw new Error(`transcribe HTTP ${res.status}`)
    const data = (await res.json()) as { transcript?: string }
    if (data.transcript) setInputText(data.transcript)
  }

  const apply = async () => {
    if (!proposalId) return
    setStep("applying")
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals/${proposalId}/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ apply_immediately: true }),
      })
      if (!res.ok) throw new Error(`confirm HTTP ${res.status}`)
      const data = await res.json()
      const misPlanId = extractMisPlanId(data)
      if (misPlanId && !plan.mis_plan_id) {
        await fetch(`${apiBase}/staff/constructor/plans/${plan.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...authHeader },
          body: JSON.stringify({ mis_plan_id: misPlanId }),
        })
      }
      setStep("done")
      setMessage(misPlanId ? `План MIS создан: ${misPlanId.slice(0, 8)}…` : "План применён")
      void persistCheckpoint({ step: "done", input_text: inputText, proposal_id: proposalId, preview, applied_mis_plan_id: misPlanId, last_error: null })
      onApplied()
    } catch (e) {
      const err = String(e)
      setStep("error")
      setMessage(err)
      void persistCheckpoint({ step: "error", input_text: inputText, proposal_id: proposalId, preview, last_error: err })
    }
  }

  return (
    <div className="space-y-2 text-sm">
      <p className="text-xs text-slate-500">
        {actionKey === FILL_ACTION
          ? "Составление нового плана в MIS по голосу/тексту."
          : "Коррекция связанного MIS-плана голосом/текстом."}
      </p>
      <div className="flex flex-wrap gap-2 text-xs">
        <label className="flex items-center gap-1">
          <input type="radio" checked={outputMode === "staged"} onChange={() => setOutputMode("staged")} />
          По этапам
        </label>
        <label className="flex items-center gap-1">
          <input type="radio" checked={outputMode === "flat"} onChange={() => setOutputMode("flat")} />
          Общий список
        </label>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={workspaceTarget === "classic"}
            onChange={() => setWorkspaceTarget("classic")}
          />
          Классический
        </label>
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={workspaceTarget === "constructor"}
            onChange={() => setWorkspaceTarget("constructor")}
          />
          Конструктор
        </label>
      </div>
      {diagnosticsLinked ? (
        <label className="flex items-center gap-1 text-xs text-slate-600">
          <input type="checkbox" checked={useDiagnocat} onChange={(e) => setUseDiagnocat(e.target.checked)} />
          Учитывать находки Diagnocat
        </label>
      ) : null}
      <textarea
        className="w-full rounded border border-slate-200 p-2 text-xs"
        rows={4}
        placeholder="Опишите план лечения…"
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <VoiceMicButton
          disabled={!misPatientId || step === "proposing"}
          onCaptured={(f) =>
            void transcribe(f)
              .then(() => propose(inputText))
              .catch((e) => {
                setStep("error")
                setMessage(String(e))
              })
          }
        />
        <button
          type="button"
          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
          disabled={!misPatientId || step === "proposing" || !inputText.trim()}
          onClick={() => void propose(inputText)}
        >
          {step === "proposing" ? "Формируем…" : "Сформировать"}
        </button>
      </div>
      {step === "preview" && preview ? (
        <div className="space-y-2 rounded border border-slate-200 bg-slate-50 p-2">
          <pre className="max-h-40 overflow-auto text-[10px]">{JSON.stringify(preview, null, 2)}</pre>
          <button
            type="button"
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs text-white"
            onClick={() => void apply()}
          >
            Применить
          </button>
        </div>
      ) : null}
      {step === "done" ? <p className="text-xs text-emerald-700">{message}</p> : null}
      {step === "error" ? <p className="text-xs text-red-600">{message}</p> : null}
    </div>
  )
}
